import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { fetchCurrentProject } from '@chegg-wt/core/state/currentProject/actions'
import { FetchCitationsPayload } from '@chegg-wt/core/state/bibliography/types'
import { AppState } from '@chegg-wt/core/state'
import { Config } from '@chegg-wt/core/config'
import ClientOnlyPortal from '@chegg-wt/core/components/ClientOnlyPortal'
import { maybePluralizeWithNum } from '@chegg-wt/core/utils/language'
import {
  COPY_SUCCESS_MODAL,
  EXPORT_MODAL,
  DELETE_MODAL,
  COPY_SUCCESS_INTERSTITIAL_MODAL,
} from '@chegg-wt/core/state/modals/constants'
import { OpenModalPayload } from '@chegg-wt/core/state/modals/types'
import {
  useOimAuthEventHandlers,
  useOimOptIntegrationHandlers,
} from '@chegg-wt/core/hooks/useOimEventHandlers'
import { AuthSuccessGrant } from '@chegg/wtf-components'
import { BibliographyUpsellCopy } from '@chegg-wt/core/types/features'
import PlagiarismUpsellCard from '@chegg-wt/core/components//PlagiarismUpsellCard'
import {
  INTERSTITIAL_AD_CTFM_EB_TEST_CELL,
  VIEWPAGE_PLAGIARISM_EXPORT,
} from '@chegg-wt/core/state/optimizely/constants'
import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import MaxWidthContainer from '@chegg-wt/core/components/MaxWidthContainer'
import {
  ExportType,
  ExportResult,
} from '@chegg-wt/core/components/Modals/ExportModal/types'
import { useToast } from '@chegg-wt/core/hooks/useToast'
import { SuccessToast } from '@chegg-wt/core/components/Toast'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { ViewPageExitIntents } from '@chegg-wt/core/components/ViewPageExitIntents'
import { ViewPageExitIntentExperiences } from '@chegg-wt/core/components/ViewPageExitIntents/types'
import { MainWrapper } from '@chegg-wt/core/components/Layouts/styled'
import { copyAll } from '@chegg-wt/core/utils/copy'
import { appScroll } from '@chegg-wt/core/utils/scroll'
import { Citation } from '@chegg-wt/core/types/redux-citation-api'
import { CitationCopy, BibliographyB1Copy } from '@chegg-wt/core/types/copy'
import { NextJsRoute } from '@chegg-wt/core/types/core'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { login } from '@chegg-wt/core/state/user/actions'
import { initializedSelector } from '@chegg-wt/core/state/app/selectors'
import { selectedCitationsSelector } from '@chegg-wt/core/state/bibliography/selectors'
import {
  currentProjectSetInProgressSelector,
  currentStyleSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import {
  isAuthLoadingSelector,
  userRoleSelector,
} from '@chegg-wt/core/state/user/selectors'
import { adConfigVersionSelector } from '@chegg-wt/core/state/featureFlags/selectors'
import { UserRole } from '@chegg-wt/core/constants/user'
import UploadWidget from './UploadWidget'
import ButtonContainer from './ButtonContainer'
import CitationList from './CitationList'
import * as Styled from './styled'
import CheckboxMenu from './CheckboxMenu'
import optimizelyMapper from './utils/optimizelyMapper'
import { safeParseJson } from './utils/JsonParser'

interface Props {
  currentProjectSetInProgress: boolean
  initialized: boolean
  currentStyle: string
  adConfigVersion: string
  userRole: UserRole
  selectedCitations: Citation[]
  fetchCurrentProject: (payload: FetchCitationsPayload) => void
  openModal: (payload: OpenModalPayload) => void
  login: (authSuccessGrant: AuthSuccessGrant, redirectTo?: NextJsRoute) => void
  isAuthLoading: boolean
  copy?: {
    bibliographyB1?: BibliographyB1Copy
    upsellCopy?: BibliographyUpsellCopy
    citationCopy?: CitationCopy
  }
}

export const TESTID_VIEWPAGE = 'viewpage'
export const INLINE_UPLOAD_WIDGET_ID = 'inline-upload-widget'
export const UPGRADE_AUTH_MODAL_CTA = 'upgrade-auth-modal-cta'

export const BibliographyContainer: React.FunctionComponent<Props> = ({
  currentProjectSetInProgress,
  initialized,
  selectedCitations,
  fetchCurrentProject,
  openModal,
  login,
  isAuthLoading,
  copy,
  currentStyle,
  adConfigVersion,
  userRole,
}) => {
  const { toastState, showToast: showSuccessToast, hideToast } = useToast()

  const { toBibliography } = useRoutesContext()
  const router = useRouter()
  const { id: projectId } = router.query as { id: string }

  useEffect(() => {
    hideToast()
  }, [projectId])

  useOimAuthEventHandlers({ login, isAuthLoading })
  useOimOptIntegrationHandlers(optimizelyMapper)

  useEffect(() => {
    if (initialized && !currentProjectSetInProgress) {
      if (projectId) fetchCurrentProject({ projectId })
      else fetchCurrentProject({})
    }
  }, [initialized, projectId, fetchCurrentProject, currentProjectSetInProgress])

  const handleCopy = (returnFocusId: string) => {
    copyAll(selectedCitations)
    openModal({
      name:
        userRole !== 'mybibpro' &&
        (Config.showInterstitialAd ||
          adConfigVersion === INTERSTITIAL_AD_CTFM_EB_TEST_CELL)
          ? COPY_SUCCESS_INTERSTITIAL_MODAL
          : COPY_SUCCESS_MODAL,
      data: {
        text: maybePluralizeWithNum(selectedCitations.length, 'citation', 'citations'),
        checkboxMenu: true,
        returnFocusSelector: `#${returnFocusId}`,
      },
    })
  }

  const handleExportComplete = (exportResult: ExportResult) => {
    switch (exportResult.type) {
      case ExportType.move:
        fetchCurrentProject({ projectId }) // refetch
        appScroll({ top: 0, left: 0, behavior: 'auto' }) // scroll to top for success message
        showSuccessToast(
          copy?.citationCopy?.toastMessages?.moveCitations(
            exportResult.citations.length,
            exportResult.projectName,
            toBibliography({ style: currentStyle, projectId: exportResult.projectId })
          )
        )
        break
      case ExportType.export:
      default:
        break
    }
  }

  const handleExport = (citations: Citation[]) => {
    openModal({
      data: { selectedCitations: citations, handleComplete: handleExportComplete },
      name: EXPORT_MODAL,
    })
  }

  const handleDelete = () => {
    openModal({
      name: DELETE_MODAL,
      data: {
        citations: selectedCitations,
        resourceType: 'citations',
      },
    })
  }

  const { isEnabled, variables } = useSafeOptimizelyFeature(VIEWPAGE_PLAGIARISM_EXPORT)
  const { withC1Removed, withPlagiarismOrExpertCard } = variables
  const data = safeParseJson(withPlagiarismOrExpertCard as string)
  const showPlagiarismUpsellCard = data.showExpertCopy || data.showPlagiarismCopy
  const copyToShow = data.showExpertCopy
    ? copy?.upsellCopy?.expertProofRead
    : copy?.upsellCopy?.plagiarism
  const { toUpgrade } = useRoutesContext()
  return (
    <MaxWidthContainer>
      <Styled.Wrapper data-test={TESTID_VIEWPAGE}>
        <SuccessToast position="none" message={toastState.message} onClear={hideToast} />
        <MainWrapper>
          <Styled.PaperContainer data-test="bibliography-field">
            <Styled.InnerWrapper>
              <ButtonContainer onExport={handleExport} />
              <CitationList />
            </Styled.InnerWrapper>
          </Styled.PaperContainer>
        </MainWrapper>
        {!withC1Removed && <Styled.Advert placement="C1" />}
        <Styled.WidgetContainer id={INLINE_UPLOAD_WIDGET_ID} data-test="widget-container">
          {Config.inlineUploadWidget && <UploadWidget />}
        </Styled.WidgetContainer>
        {isEnabled && showPlagiarismUpsellCard && copyToShow ? (
          <Styled.PlagiarismCardContainer>
            <PlagiarismUpsellCard
              route={data.showExpertCopy ? { href: Config.expertCheckUrl } : toUpgrade()}
              copy={copyToShow}
            />
          </Styled.PlagiarismCardContainer>
        ) : null}
        <Styled.Advert placement="C2" />
        <ClientOnlyPortal selector="#checkbox-menu">
          <CheckboxMenu
            onCopy={handleCopy}
            onExport={() => handleExport(selectedCitations)}
            onDelete={handleDelete}
          />
        </ClientOnlyPortal>
      </Styled.Wrapper>
      <ViewPageExitIntents
        configuredExperience={Config.viewPageExitIntent as ViewPageExitIntentExperiences}
      />
    </MaxWidthContainer>
  )
}

const mapStateToProps = (state: AppState) => ({
  initialized: initializedSelector(state),
  selectedCitations: selectedCitationsSelector(state),
  isAuthLoading: isAuthLoadingSelector(state),
  currentStyle: currentStyleSelector(state),
  currentProjectSetInProgress: currentProjectSetInProgressSelector(state),
  adConfigVersion: adConfigVersionSelector(state),
  userRole: userRoleSelector(state),
})

const mapDispatchToProps = {
  fetchCurrentProject,
  openModal,
  login,
}

export default connect(mapStateToProps, mapDispatchToProps)(BibliographyContainer)
